import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@repo/shared/utils";

type CollapsibleSideBarProps = {
  collapsed?: boolean;
  defaultCollapsed?: boolean;
  onToggle?: (collapsed: boolean) => void;
  expandedWidth?: number | string;
  collapsedWidth?: number | string;
  className?: string;
  contentClassName?: string;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  children?: React.ReactNode;
};

const toSize = (v: number | string | undefined, fallback: string) =>
  v === undefined ? fallback : typeof v === "number" ? `${v}px` : v;

export const CollapsibleSideBar = ({
  collapsed: c,
  defaultCollapsed = false,
  onToggle,
  expandedWidth = 320,
  collapsedWidth = 56,
  className,
  contentClassName,
  header,
  footer,
  children,
}: CollapsibleSideBarProps) => {
  const isCtrl = typeof c === "boolean";
  const [internal, setInternal] = React.useState(defaultCollapsed);
  const collapsed = isCtrl ? (c as boolean) : internal;

  const width = collapsed
    ? toSize(collapsedWidth, "56px")
    : toSize(expandedWidth, "320px");

  const toggle = () => {
    if (!isCtrl) setInternal((v) => !v);
    onToggle?.(!collapsed);
  };

  return (
    <aside
      data-collapsed={collapsed}
      className={cn(
        "relative flex h-full flex-col bg-accent/20 border-r",
        "transition-[width] duration-300 ease-in-out",
        "overflow-hidden overflow-x-hidden",
        className
      )}
      style={{ width, minWidth: width }}
      aria-label="Sidebar navigation"
    >
      {header && <div className="px-4 py-3 border-b">{header}</div>}

      <div
        className={cn(
          "flex-1 overflow-y-auto",
          "overflow-x-hidden",
          contentClassName
        )}
      >
        {children}
      </div>

      {footer && <div className="px-4 py-3 border-t">{footer}</div>}

      <button
        type="button"
        onClick={toggle}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        className={cn(
          "absolute top-2 z-10 inline-flex h-6 w-6 items-center justify-center",
          "right-2",
          "rounded-full border bg-white shadow-sm hover:bg-accent/30"
        )}
      >
        {collapsed ? (
          <ChevronRight className="h-4 w-4" />
        ) : (
          <ChevronLeft className="h-4 w-4" />
        )}
      </button>
    </aside>
  );
};
